import { type ReactNode, useState } from 'react';
import { AtomicMark } from '@/components/visuals/AtomicMark';

interface ButtonProps {
  children: ReactNode;
  href?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'md' | 'lg';
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  chevron?: boolean;
}

export function Button({
  children,
  href,
  variant = 'primary',
  size = 'md',
  onClick,
  type,
  disabled,
  loading,
  chevron = false,
}: ButtonProps) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontFamily: 'var(--font-body)',
    fontWeight: 500,
    fontSize: size === 'lg' ? 16 : 15,
    padding: size === 'lg' ? '16px 28px' : '13px 22px',
    borderRadius: 6,
    transition:
      'background 160ms var(--ease-out), border-color 160ms var(--ease-out), transform 120ms var(--ease-out), box-shadow 240ms var(--ease-out), color 160ms var(--ease-out)',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    border: '1px solid transparent',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    lineHeight: 1,
    transform: pressed
      ? 'translateY(0)'
      : hover && !disabled && !loading
        ? 'translateY(-1px)'
        : 'translateY(0)',
    opacity: disabled || loading ? 0.7 : 1,
  };

  let style: React.CSSProperties = { ...base };
  if (variant === 'primary') {
    style = {
      ...style,
      background: pressed
        ? 'var(--accent-pressed)'
        : hover
          ? 'linear-gradient(135deg, var(--radium-300) 0%, var(--accent-hover) 100%)'
          : 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%)',
      color: 'var(--ink-on-accent)',
      borderColor: 'transparent',
      boxShadow: hover
        ? '0 0 0 1px rgba(255,255,255,0.18) inset, 0 1px 0 rgba(255,255,255,0.35) inset, 0 -1px 0 rgba(0,0,0,0.15) inset, 0 12px 36px var(--radium-glow), 0 0 0 1px var(--radium-300)'
        : '0 0 0 1px rgba(255,255,255,0.14) inset, 0 1px 0 rgba(255,255,255,0.28) inset, 0 -1px 0 rgba(0,0,0,0.15) inset, 0 8px 24px var(--radium-glow)',
    };
  } else if (variant === 'secondary') {
    style = {
      ...style,
      background: hover
        ? 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
      backdropFilter: 'blur(16px) saturate(155%)',
      WebkitBackdropFilter: 'blur(16px) saturate(155%)',
      color: 'var(--ink-primary)',
      borderColor: hover ? 'var(--accent)' : 'rgba(255, 255, 255, 0.14)',
      boxShadow: hover
        ? 'inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 28px rgba(0,0,0,0.35), 0 0 28px -6px var(--radium-glow)'
        : 'inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 12px rgba(0,0,0,0.25)',
    };
  } else if (variant === 'ghost') {
    style = {
      ...style,
      background: 'transparent',
      color: 'var(--accent)',
      padding: '0',
      border: 'none',
      transform: 'none',
      boxShadow: 'none',
    };
  }

  const chevronEl = chevron ? (
    <span
      style={{
        display: 'inline-block',
        transition: 'transform 160ms var(--ease-out)',
        transform: hover ? 'translateX(3px)' : 'translateX(0)',
      }}
    >
      →
    </span>
  ) : null;

  const inner = (
    <>
      {loading ? <AtomicMark size={14} tone="currentColor" spinning /> : null}
      <span>{children}</span>
      {chevronEl}
    </>
  );

  const handlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => {
      setHover(false);
      setPressed(false);
    },
    onMouseDown: () => setPressed(true),
    onMouseUp: () => setPressed(false),
  };

  if (href) {
    return (
      <a href={href} style={style} {...handlers}>
        {inner}
      </a>
    );
  }
  return (
    <button
      type={type || 'button'}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading ? true : undefined}
      style={style}
      {...handlers}
    >
      {inner}
    </button>
  );
}
